// screens-game.tsx - the public page for one showcased game: trailer,
// screenshot gallery, and the play/wiki/developer links around them.
//
// The showcase grid deliberately stays a grid of cards; this is where a
// visitor who wants to know what a game actually looks like ends up, and it
// is the only surface that renders the trailer and the full gallery.
// Exports: ScreenGame, TrailerPlayer, ScreenshotGallery
import { useCallback, useEffect, useState } from 'react';

import { Link } from '../design/router';
import { CenteredLoading, MarketingFooter, MarketingNav, NotFoundScreen, PlayCta, PlayLinkWrap } from '../components/ui-shared';
import { ImageSlot } from '../components/ImageSlot';
import { listPublicGames, type PublicGame } from '../play/playClient';
import {
  formatDuration,
  keyArt,
  loadOriginalsMedia,
  mediaSrc,
  mergeMedia,
  trailerPoster,
  type GameMedia,
  type MediaImage,
} from './media';

/**
 * The trailer, as a real `<video>` with its poster frame showing until the
 * visitor asks for it.
 *
 * Deliberately not autoplaying: a marketing video that starts by itself is
 * the single most complained-about pattern on a store page, and an
 * autoplaying video with sound is blocked by every browser anyway. `preload`
 * is `none` until the first play, so a visitor who never presses play never
 * downloads several megabytes of video.
 */
export function TrailerPlayer({ media, name }: { media: GameMedia; name: string }) {
  const [started, setStarted] = useState(false);
  const src = mediaSrc(media.trailer?.url);
  const poster = trailerPoster(media);
  const duration = formatDuration(media.trailer?.duration_sec);
  if (!src) return null;

  return (
    <div
      className="ss-card"
      style={{ padding: 0, overflow: 'hidden', position: 'relative', background: '#000', borderColor: 'var(--ss-border)' }}
    >
      <video
        src={started ? src : undefined}
        poster={poster ?? undefined}
        controls={started}
        preload="none"
        playsInline
        // `controls` only appears once playback has been asked for, so the
        // resting state is a clean poster frame rather than a browser chrome
        // bar over the key art.
        style={{ width: '100%', display: 'block', aspectRatio: '16 / 9', background: '#000' }}
        aria-label={`${name} trailer`}
        onPlay={() => setStarted(true)}
      />
      {!started && (
        <button
          type="button"
          onClick={(event) => {
            setStarted(true);
            const video = event.currentTarget.parentElement?.querySelector('video');
            // The src only binds on this state change, so playback has to
            // wait for the element to actually have a source.
            requestAnimationFrame(() => void video?.play().catch(() => {}));
          }}
          aria-label={`Play the ${name} trailer`}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            border: 0,
            background: 'linear-gradient(180deg, rgba(11,11,16,0.05), rgba(11,11,16,0.55))',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              width: 74,
              height: 74,
              borderRadius: '50%',
              background: 'var(--ss-accent)',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" fill="#fff" />
            </svg>
          </span>
          {duration && (
            <span style={{ position: 'absolute', right: 16, bottom: 14, fontSize: 12.5, fontWeight: 500, opacity: 0.9 }}>
              Trailer · {duration}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * The screenshot gallery: a large active shot with a thumbnail strip.
 *
 * Keyboard driven as well as clickable - left/right move through the strip
 * while it has focus, because a gallery a visitor cannot page through
 * without a mouse is not a gallery.
 */
export function ScreenshotGallery({ shots, name }: { shots: MediaImage[]; name: string }) {
  const [index, setIndex] = useState(0);
  const step = useCallback(
    (delta: number) => setIndex((current) => (current + delta + shots.length) % shots.length),
    [shots.length],
  );
  if (!shots.length) return null;
  const active = shots[Math.min(index, shots.length - 1)]!;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        role="group"
        aria-label={`${name} screenshots`}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
        }}
        style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--ss-border-soft)', background: 'var(--ss-panel-strong)' }}
      >
        <img
          src={mediaSrc(active.url) ?? ''}
          alt={active.alt || `${name} screenshot ${index + 1} of ${shots.length}`}
          style={{ width: '100%', display: 'block', aspectRatio: '16 / 9', objectFit: 'contain', background: '#0b0b10' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
          {shots.map((shot, i) => (
            <button
              key={shot.url}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show screenshot ${i + 1}`}
              aria-current={i === index}
              style={{
                flex: '0 0 auto',
                width: 116,
                height: 66,
                padding: 0,
                borderRadius: 8,
                overflow: 'hidden',
                cursor: 'pointer',
                background: '#0b0b10',
                border: i === index ? '2px solid var(--ss-accent)' : '1px solid var(--ss-border-soft)',
                opacity: i === index ? 1 : 0.66,
              }}
            >
              <img src={mediaSrc(shot.url) ?? ''} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </button>
          ))}
        </div>
        <span style={{ color: 'var(--ss-text-3)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
          {index + 1} / {shots.length}
        </span>
      </div>
    </div>
  );
}

export function ScreenGame({ slug }: { slug: string }) {
  const [game, setGame] = useState<PublicGame | null>(null);
  const [media, setMedia] = useState<GameMedia | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listPublicGames().catch(() => [] as PublicGame[]), loadOriginalsMedia()])
      .then(([rows, originals]) => {
        if (cancelled) return;
        const row = rows.find((g) => g.slug.toLowerCase() === slug.toLowerCase()) ?? null;
        setGame(row);
        setMedia(mergeMedia(row?.media, originals[slug.toLowerCase()]));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="ss-screen">
        <MarketingNav active="Showcase" />
        <CenteredLoading />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="ss-screen">
        <MarketingNav active="Showcase" />
        <NotFoundScreen
          style={{ minHeight: '60vh' }}
          title="No game here."
          body="This link doesn't match a published game. It may be mistyped, or the game may have been unpublished."
          ctas={[{ label: 'Browse the showcase', to: '/showcase' }]}
        />
        <MarketingFooter />
      </div>
    );
  }

  const art = keyArt(media);
  const devName = game.dev?.studio_name || 'a Soulstack creator';

  return (
    <div className="ss-screen">
      <MarketingNav active="Showcase" />
      <section style={{ padding: '56px 28px 24px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 26 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Link to="/showcase" className="ss-eyebrow" style={{ color: 'var(--ss-text-3)' }}>
              &lsaquo; Showcase
            </Link>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 7vw, 3rem)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {game.name}
                </h1>
                {media?.tagline && (
                  <p style={{ color: 'var(--ss-text-2)', fontSize: 16.5, maxWidth: 640, lineHeight: 1.6, textWrap: 'pretty' }}>
                    {media.tagline}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {game.dev?.slug ? (
                    <Link to={'/dev/' + game.dev.slug} style={{ fontSize: 13, color: 'var(--ss-text-3)' }}>
                      by <span style={{ color: 'var(--ss-accent)', fontWeight: 500 }}>{devName}</span>
                    </Link>
                  ) : (
                    <span style={{ fontSize: 13, color: 'var(--ss-text-3)' }}>by {devName}</span>
                  )}
                  {(media?.tags ?? []).map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: 10.5, fontWeight: 500, color: 'var(--ss-text-3)',
                        border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '2px 9px',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <PlayCta to={'/play/' + game.slug}>Play now</PlayCta>
                {game.wiki && (
                  <a className="ss-btn ss-btn--ghost" href={game.wiki.url} target="_blank" rel="noreferrer">
                    Wiki
                  </a>
                )}
              </div>
            </div>
          </div>

          {media?.trailer ? (
            <TrailerPlayer media={media} name={game.name} />
          ) : art ? (
            <PlayLinkWrap to={'/play/' + game.slug} className="thumb-wrap" style={{ display: 'block' }}>
              <ImageSlot
                shape="rounded"
                radius={14}
                src={mediaSrc(art.url) ?? undefined}
                alt={art.alt || `${game.name} key art`}
                placeholder={game.name + ' key art'}
                style={{ width: '100%', aspectRatio: '16 / 9', display: 'block', pointerEvents: 'none' }}
              />
            </PlayLinkWrap>
          ) : null}

          {!!media?.screenshots.length && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 6 }}>
              <span className="ss-eyebrow">Screenshots</span>
              <ScreenshotGallery shots={media.screenshots} name={game.name} />
            </div>
          )}

          {!media && (
            <p style={{ color: 'var(--ss-text-3)', fontSize: 14 }}>
              This game hasn't published screenshots or a trailer yet.
            </p>
          )}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
